"use client";

import React, { useRef, useState, useCallback } from 'react';
import styles from './_gc-step-patient-profile.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import { FormInputText, FormInputCheckbox } from '@/components/form';
import Button from '@/components/button';
import { GC_CALC_STATUS, GC_CALC_POPULATION, GC_CALC_TREATMENT_GOALS } from '@/data/gc-calculator-data';

const AGE_ERROR_NUMERIC = 'Please enter a numeric age (e.g., 37).';
const AGE_ERROR_UNDER_4 = 'CRENESSITY is approved for patients aged 4 and older. This tool is intended for use in patients 4+.';
const AGE_ERROR_OVER_90 = 'This calculator cannot be used for patients aged 90 and older because of HIPAA requirements.';

const TREATMENT_GOALS = GC_CALC_TREATMENT_GOALS;

const DISCLAIMER = (
  <>
    <div className={styles.disclaimer_callout}>
      <p className="italic !text-md !font-extrabold !leading-5">
        This calculator is intended for US healthcare professionals only.
      </p>
      <p className="italic !text-md !font-extrabold !leading-5">
        Patients should talk to their healthcare provider before making any changes to their treatment plan.
      </p>
    </div>
    <p className="italic !text-sm !leading-5 !text-hcp-charcoal">
      This calculator uses direction from expert opinion on the approach to GC reduction
      with initiation of CRENESSITY in adult and pediatric patients with classic
      CAH. As patients vary in clinical presentation, this calculator may not be
      appropriate for every person with CAH. Adjustments to GC doses should be
      individualized based on each patient&rsquo;s clinical needs and the
      clinician&rsquo;s experience.
    </p>
  </>
);

function isAgeValid(val) {
  const n = parseInt(val, 10);
  return !isNaN(n) && n >= 4 && n <= 89;
}

/**
 * @description GC OPTIMIZE™ Calculator — Step 1: Patient Profile
 * Collects: patient age, CRENESSITY status (Starting / Continuing),
 * and treatment goals (multi-select checkboxes).
 * Population (adult/pediatric) is derived from age (≥18 → adult).
 *
 * Form values are tracked in refs to avoid re-renders on every keystroke.
 * Only `isNextDisabled` is state — it changes only when validity flips.
 */
function GcStepPatientProfile({ state, dispatch, onBack, onNext, isReview, stepNumber }) {
  // ── Refs (no re-render on change) ───────────────────────────────────
  const ageRef    = useRef(state.formValues.age           ?? '');
  const statusRef = useRef(state.formValues.status        ?? '');
  const goalsRef  = useRef(state.formValues.treatmentGoals ?? []);
  const ageInputRef = useRef(null);

  // ── Single state: button enabled/disabled ─────────────────────────────
  const [isNextDisabled, setIsNextDisabled] = useState(() =>
    !isAgeValid(ageRef.current) || !statusRef.current || goalsRef.current.length === 0
  );
  const [ageErrorMsg, setAgeErrorMsg] = useState(AGE_ERROR_NUMERIC);

  const validate = useCallback(() => {
    setIsNextDisabled(
      !isAgeValid(ageRef.current) || !statusRef.current || goalsRef.current.length === 0
    );
  }, []);

  const handleAgeValidate = useCallback(({ valid: isValidFromComponent }) => {
    const val = ageRef.current;
    if (isValidFromComponent && val !== '') {
      const n = parseInt(val, 10);
      if (!isNaN(n) && n < 4) {
        setAgeErrorMsg(AGE_ERROR_UNDER_4);
        ageInputRef.current?.setValid(false);
      } else if (!isNaN(n) && n >= 90) {
        setAgeErrorMsg(AGE_ERROR_OVER_90);
        ageInputRef.current?.setValid(false);
      }
    } else if (!isValidFromComponent) {
      // Non-numeric or empty — use default message
      setAgeErrorMsg(AGE_ERROR_NUMERIC);
    }
  }, []);

  const handleGoalToggle = useCallback((id) => {
    const prev = goalsRef.current;
    goalsRef.current = prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id];
    validate();
  }, [validate]);

  const handleSave = useCallback(() => {
    const ageNum = parseInt(ageRef.current, 10);
    const population = ageNum >= 18 ? GC_CALC_POPULATION.ADULT : GC_CALC_POPULATION.PEDIATRIC;
    const newStatus = statusRef.current;
    // Determine if this is a re-routing (population or status changed from
    // a previously saved value) — tells goNext to force-advance forward.
    const isReroute = state.population !== null &&
      (population !== state.population || newStatus !== state.status);
    // Single atomic dispatch — detects routing changes (population / status)
    // and clears all downstream step data when they differ from saved values.
    dispatch({
      type:           'SAVE_PATIENT_PROFILE',
      age:            ageRef.current,
      status:         newStatus,
      treatmentGoals: goalsRef.current,
      population,
    });
    // Pass the just-dispatched values so goNext can resolve the correct path
    // without waiting for the reducer re-render. forceReadvance ensures the
    // wizard advances to step 2 (rather than just collapsing) after a re-route.
    onNext({ population, status: newStatus, forceReadvance: isReroute });
  }, [dispatch, onNext, state.population, state.status]);

  return (
    <GcStepShell
      title="Patient Profile"
      onBack={onBack}
      onNext={handleSave}
      showBack={false}
      isNextDisabled={isNextDisabled}
      isReview={isReview}
      stepNumber={stepNumber}
      disclaimer={DISCLAIMER}
    >
      {/* ── Patient age ─────────────────────────────────────────────── */}
      <FormInputText
        ref={ageInputRef}
        name="gc-patient-age"
        label="Patient age"
        placeholder="E.g., 37"
        autoformatNumericOnly
        required
        inputMode="numeric"
        customClass={styles.age_input}
        defaultValue={state.formValues.age ?? ''}
        onChange={(e) => { ageRef.current = e.target.value; validate(); }}
        onValidate={handleAgeValidate}
        errorMessage={ageErrorMsg}
        maxLength={2}
      />

      {/* ── CRENESSITY status ────────────────────────────────────────── */}
      <fieldset className={styles.field_group}>
        <legend className={styles.field_label}>*CRENESSITY status</legend>

        <FormInputCheckbox
          name="gc-status-starting"
          typeRadio
          radioGroupName="gc-status"
          defaultChecked={statusRef.current === GC_CALC_STATUS.STARTING}
          onChange={() => { statusRef.current = GC_CALC_STATUS.STARTING; validate(); }}
        >
          Starting a patient
        </FormInputCheckbox>

        <FormInputCheckbox
          name="gc-status-continuing"
          typeRadio
          radioGroupName="gc-status"
          defaultChecked={statusRef.current === GC_CALC_STATUS.CONTINUING}
          onChange={() => { statusRef.current = GC_CALC_STATUS.CONTINUING; validate(); }}
        >
          Patient already on CRENESSITY
        </FormInputCheckbox>
      </fieldset>

      {/* ── Treatment goals ──────────────────────────────────────────── */}
      <fieldset className={styles.field_group}>
        <legend className={styles.field_label}>*Treatment goals</legend>
        <p className={styles.sub_label}>Select all that apply</p>

        {TREATMENT_GOALS.map(({ id, label }) => (
          <FormInputCheckbox
            key={id}
            name={`gc-goal-${id}`}
            defaultChecked={goalsRef.current.includes(id)}
            onChange={() => handleGoalToggle(id)}
          >
            {label}
          </FormInputCheckbox>
        ))}
      </fieldset>
      <p className="italic !text-sm !mb-0 !text-hcp-charcoal">Fields marked with an * are required.</p>
      <p className="italic !text-md text !leading-6 !text-hcp-charcoal">
        This calculator has been adapted from expert opinions published in the <span
          className="not-italic">Journal of Clinical Endocrinology &amp; Metabolism</span> on glucocorticoid
        dose reduction after initiating CRENESSITY in patients with classic CAH.
        For additional context, please refer to the full <Button
          variant="link"
          href="https://academic.oup.com/jcem/advance-article/doi/10.1210/clinem/dgag147/8571825?login=false"
          targetBlank
          leaveSiteLink
          customClass="!underline !italic !leading-6"
          data-gtm-event-category="External Link"
          data-gtm-event-action="Click"
          data-gtm-event-label="Adult Publication"
        >adult</Button> and <Button
          variant="link"
          href="https://academic.oup.com/jcem/advance-article/doi/10.1210/clinem/dgag192/8667182?searchresult=1&login=false"
          targetBlank
          leaveSiteLink
          customClass="!underline !italic !leading-6"
          data-gtm-event-category="External Link"
          data-gtm-event-action="Click"
          data-gtm-event-label="Adult Publication"
        >pediatric</Button> publications.
      </p>
    </GcStepShell>
  );
}

export default GcStepPatientProfile;
